/**
 * beosSlice — source of truth for the Banquet Event Orders list (Figma 7.1)
 * and the open detail view (Figma 7.2).
 *
 * Wired to the live BEO API (`events/api/v1/beos/…`, see api-integration.md):
 *  - `beos` is hydrated from `getBeosListSSR` via `BeosInitializer`.
 *  - Opening a row fetches `getBeoDetailAction(bookingId)` into `detail`.
 *  - The timeline + staff shown in the detail are read straight off `detail`
 *    (`detail.timeline` / `detail.staff`); mutations go through the BEO server
 *    actions (`useBeos`) and refetch the detail, so there are no local
 *    add/edit/delete reducers — the API is authoritative.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { BeoDetail } from "@/components/beos/detail/types";
import type { Beo } from "@/components/beos/types";
import type { SelectOption } from "@/Shared/CSelect/CSelect";

export interface BeosState {
  /** BEO workspace list (hydrated by BeosInitializer / refetched after writes). */
  beos: Beo[];
  /** True until the list is hydrated (set false by BeosInitializer). */
  loading: boolean;
  /** When set, the detail view (7.2) is shown for this booking id. */
  beoDetailId: string | null;
  /** The composed run-sheet for the open booking (null until fetched). */
  detail: BeoDetail | null;
  /** True while the detail is being fetched / refetched. */
  detailLoading: boolean;
  /** Assignable team members for the Assign/Change Staff dropdown (SSR-hydrated). */
  assignees: SelectOption[];
  /**
   * True until the assignees stream lands. Distinct from `assignees.length === 0`
   * so a tenant with genuinely zero staff resolves to an empty picker state
   * instead of a perpetual "Loading staff…".
   */
  assigneesLoading: boolean;
}

const initialState: BeosState = {
  beos: [],
  loading: true,
  beoDetailId: null,
  detail: null,
  detailLoading: false,
  assignees: [],
  assigneesLoading: true,
};

const beosSlice = createSlice({
  name: "beos",
  initialState,
  reducers: {
    /** Replace the list (SSR hydration / post-mutation refetch). */
    setBeos(state, action: PayloadAction<Beo[]>) {
      state.beos = action.payload;
    },
    setBeosLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    /**
     * Reset the list to its loading state — dispatched when leaving the route so
     * a re-entry shows the loading skeleton instead of flashing the previous
     * visit's persisted (stale) list before the fresh SSR read re-hydrates. The
     * Redux store survives navigation.
     */
    resetBeosList(state) {
      state.beos = [];
      state.loading = true;
    },

    /** Open the detail for a booking — clears any stale detail + flags loading. */
    openBeoDetail(state, action: PayloadAction<string>) {
      state.beoDetailId = action.payload;
      state.detail = null;
      state.detailLoading = true;
    },
    closeBeoDetail(state) {
      state.beoDetailId = null;
      state.detail = null;
      state.detailLoading = false;
    },
    /** Hydrate the open booking's detail (or null on a failed read). */
    setBeoDetail(state, action: PayloadAction<BeoDetail | null>) {
      state.detail = action.payload;
      state.detailLoading = false;
    },
    setBeoDetailLoading(state, action: PayloadAction<boolean>) {
      state.detailLoading = action.payload;
    },
    /** Hydrate the assignable team-member options (SSR) — settles the loading flag. */
    setBeoAssignees(state, action: PayloadAction<SelectOption[]>) {
      state.assignees = action.payload;
      state.assigneesLoading = false;
    },
  },
});

export const {
  setBeos,
  setBeosLoading,
  resetBeosList,
  openBeoDetail,
  closeBeoDetail,
  setBeoDetail,
  setBeoDetailLoading,
  setBeoAssignees,
} = beosSlice.actions;

export default beosSlice.reducer;
